/**
 * ClickReel SDK
 *
 * A lightweight SDK for embedding shoppable video widgets on any website.
 * Features: Shadow DOM isolation, responsive reel trigger, video player overlay.
 *
 * @example
 * ```html
 * <script src="https://cdn.clickreel.io/sdk/v1.js"></script>
 * <script>
 *   ClickReel.init({ siteKey: 'your-site-key' });
 * </script>
 * ```
 */

import {
  ClickReelConfig,
  ClickReelEvent,
  EventHandler,
  SDKState,
  CampaignConfig,
  VideoProgressData,
  SDK_VERSION,
  DEFAULT_API_URL,
  SDKSettings,
  InteractionNode,
  InteractionEdge,
  OverlayChoicesConfig,
} from './types';
import { initShadowDOM, destroyShadowDOM, getShadowContainer } from './shadow-dom';
import { BubbleTrigger } from './bubble';
import { VideoPlayer } from './video-player';
import { YouTubePlayer } from './youtube-player';
import { InstagramPlayer } from './instagram-player';
import { TikTokPlayer } from './tiktok-player';
import { CloudflarePlayer } from './cloudflare-player';
import { OverlayChoices } from './overlay-choices';
import { FormOverlay } from './form-overlay';
import { isMobile, teardownVideoUtils } from './video-utils';
import { logger, configureErrorReporting } from './logger';
import { initAnalyticsBridge, updateBridgeContext, fireAnalyticsBridgeEvent } from './analytics-bridge';
import { ensureGlobalMorphKeyframes } from './morph-keyframes';

// Re-export types for consumers
export * from './types';

// =============================================================================
// ClickReel SDK Class
// =============================================================================

class ClickReelSDK {
  private config: ClickReelConfig | null = null;
  private campaign: CampaignConfig | null = null;
  private state: SDKState = 'uninitialized';
  private eventHandlers: Map<ClickReelEvent, Set<EventHandler>> = new Map();
  private bubble: BubbleTrigger | null = null;
  private videoPlayer: VideoPlayer | null = null;
  private youtubePlayer: YouTubePlayer | null = null;
  private instagramPlayer: InstagramPlayer | null = null;
  private tiktokPlayer: TikTokPlayer | null = null;
  private cloudflarePlayer: CloudflarePlayer | null = null;
  private dismissedUntil: number = 0;
  private sessionId: string | null = null;
  private currentVideoIndex = 0;
  private videoOpened = false;
  private impressionFired = false;
  private hoverTracked = false;
  private showBranding = true;
  private storyDismissed = false;

  // Interactive campaign state
  private currentNodeId: string | null = null;
  private flowNodes: Map<string, InteractionNode> = new Map();
  private flowEdges: InteractionEdge[] = [];
  private overlayChoices: OverlayChoices | null = null;
  private formOverlay: FormOverlay | null = null;
  private overlayTriggered = false;

  // Sprint 003 (2026-04-29): hold references to listeners attached on the host
  // page's document/window so destroy() can clean them up. Previously these
  // self-removed on first trigger, leaking forever if destroy() ran first.
  private exitIntentHandler: ((e: MouseEvent) => void) | null = null;
  private scrollHandler: (() => void) | null = null;

  // Sprint 005 (2026-04-29): track DNS-prefetch <link> elements injected into
  // the host page's <head> so destroy() can remove them. Without this, SPA
  // re-inits accumulated duplicate <link rel="dns-prefetch"> tags forever.
  private dnsPrefetchLinks: HTMLLinkElement[] = [];

  constructor() {
    // Check for previously dismissed state
    this.loadDismissedState();
  }

  /**
   * Get the current SDK version
   */
  get version(): string {
    return SDK_VERSION;
  }

  /**
   * Get the current SDK state
   */
  get currentState(): SDKState {
    return this.state;
  }

  /**
   * Initialize the ClickReel SDK
   * @param config - Configuration options
   */
  async init(config: ClickReelConfig): Promise<void> {
    // Validate config
    if (!config || !config.siteKey) {
      logger.error('init() requires a valid siteKey');
      this.emit('error', { message: 'siteKey is required' });
      return;
    }

    if (this.state === 'ready') {
      logger.warn('SDK already initialized');
      return;
    }

    if (this.state === 'initializing') {
      logger.warn('SDK initialization in progress');
      return;
    }

    this.state = 'initializing';
    this.config = {
      apiUrl: DEFAULT_API_URL,
      debug: false,
      delay: 0,
      position: 'bottom-right',
      ...config,
    };

    logger.setDebug(this.config.debug ?? false);
    logger.debug('Initializing with config:', this.config);

    // Sprint 008 (2026-04-29): wire up remote error reporting as early as
    // possible so failures in fetchCampaignConfig (network errors, 5xx,
    // etc.) reach the server. Default: enabled. Host pages can opt out via
    // `errorReporting: { enabled: false }`. We call configureErrorReporting
    // again after fetchCampaignConfig resolves to attach the sessionId for
    // correlation.
    configureErrorReporting({
      siteKey: this.config.siteKey,
      apiUrl: this.config.apiUrl || DEFAULT_API_URL,
      enabled: this.config.errorReporting?.enabled !== false,
    });

    try {
      // Validate domain whitelist
      if (!this.validateDomain()) {
        logger.warn('Domain not in whitelist, SDK will not load');
        this.state = 'error';
        return;
      }

      // Check if dismissed (skip in demo mode — previews should always render)
      if (!this.config.demoMode && !this.config.previewMode && this.isDismissed()) {
        logger.debug('SDK dismissed until', new Date(this.dismissedUntil));
        this.state = 'ready';
        return;
      }

      // Wait for DOM to be ready
      await this.waitForDOM();

      // Fetch campaign configuration from API
      await this.fetchCampaignConfig();

      // Sprint 008 (2026-04-29): refresh error-reporting config now that the
      // server has assigned a sessionId. Lets the server-side store correlate
      // late-stage errors (player crashes, render bugs) with the originating
      // session for debugging.
      if (this.sessionId) {
        configureErrorReporting({
          siteKey: this.config.siteKey,
          apiUrl: this.config.apiUrl || DEFAULT_API_URL,
          enabled: this.config.errorReporting?.enabled !== false,
          sessionId: this.sessionId,
        });
      }

      // Initialize analytics bridge (fires events to host page's GA4/Meta Pixel)
      initAnalyticsBridge(this.config.analyticsBridge, {
        campaignId: this.campaign?.id,
        campaignName: this.campaign?.name,
        sessionId: this.sessionId || undefined,
      });

      // Prefetch DNS for video CDNs (reduces latency on first video open)
      this.addVideoDnsPrefetch();

      // Check if campaign matches current page (skip in demo mode — previews
      // run inside srcdoc iframes where window.location.pathname is "srcdoc")
      if (!this.config.demoMode && !this.config.previewMode && !this.matchesTargeting()) {
        logger.debug('Page does not match targeting rules');
        this.state = 'ready';
        return;
      }

      // Initialize Shadow DOM
      initShadowDOM();
      logger.debug('Shadow DOM initialized');

      // Pre-inject global morph keyframes so they're parsed well before any animation runs
      ensureGlobalMorphKeyframes();

      // Apply trigger delay if configured (skip for immediate triggers)
      const isImmediate = this.campaign?.trigger.type === 'immediate';
      const delay = isImmediate
        ? 0
        : this.campaign?.trigger.delaySeconds
          ? this.campaign.trigger.delaySeconds * 1000
          : this.config.delay || 0;

      if (delay > 0) {
        logger.debug(`Waiting ${delay}ms before showing bubble`);
        await this.delay(delay);
      }

      // Create and show the bubble
      this.createBubble();
      this.setupExitIntent();
      this.setupScrollTrigger();

      this.state = 'ready';
      logger.log('SDK initialized successfully (v' + SDK_VERSION + ')');
      this.emit('ready');
    } catch (error) {
      this.state = 'error';
      logger.error('Initialization failed:', error);
      this.emit('error', error);
      // Fail silently as per requirements
    }
  }

  /**
   * Subscribe to SDK events
   */
  on(event: ClickReelEvent, handler: EventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set());
    }
    this.eventHandlers.get(event)!.add(handler);
    logger.debug(`Event handler registered for: ${event}`);
  }

  /**
   * Unsubscribe from SDK events
   */
  off(event: ClickReelEvent, handler: EventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.delete(handler);
      logger.debug(`Event handler removed for: ${event}`);
    }
  }

  /**
   * Show the bubble widget
   */
  show(): void {
    if (this.bubble) {
      this.bubble.show();
      this.emit('bubble:show');
      logger.debug('Bubble shown');
    }
  }

  /**
   * Hide the bubble widget
   */
  hide(): void {
    if (this.bubble) {
      this.bubble.hide();
      this.emit('bubble:hide');
      logger.debug('Bubble hidden');
    }
  }

  /**
   * Open the video player
   */
  openVideo(): void {
    // Interactive campaign: use flow graph navigation
    if (this.campaign?.type === 'interactive' && this.campaign.flow?.rootNodeId) {
      this.videoOpened = true;
      this.storyDismissed = false;
      this.navigateToNode(this.campaign.flow.rootNodeId);
      this.hide();
      this.emit('video:open');
      return;
    }

    if (!this.campaign?.videos?.[this.currentVideoIndex]) {
      logger.warn('No video configured');
      return;
    }

    this.videoOpened = true;
    this.storyDismissed = false;

    // Fire story_start when opening first video in a multi-video story
    if ((this.campaign?.videos?.length ?? 0) > 1 && this.currentVideoIndex === 0) {
      this.trackEvent('story_start', { videoId: this.campaign!.videos[0].id });
    }

    this.createVideoPlayer();

    // Open the appropriate player (priority: Cloudflare → Instagram → TikTok → YouTube → HTML5)
    if (this.cloudflarePlayer) {
      this.cloudflarePlayer.open();
    } else if (this.instagramPlayer) {
      this.instagramPlayer.open();
    } else if (this.tiktokPlayer) {
      this.tiktokPlayer.open();
    } else if (this.youtubePlayer) {
      this.youtubePlayer.open();
    } else {
      this.videoPlayer?.open();
    }

    this.hide();
    this.emit('video:open');
  }

  /**
   * Close the video player
   */
  closeVideo(): void {
    if (this.cloudflarePlayer?.isVisible) {
      this.cloudflarePlayer.close();
    } else if (this.instagramPlayer?.isVisible) {
      this.instagramPlayer.close();
    } else if (this.tiktokPlayer?.isVisible) {
      this.tiktokPlayer.close();
    } else if (this.youtubePlayer?.isVisible) {
      this.youtubePlayer.close();
    } else if (this.videoPlayer?.isVisible) {
      this.videoPlayer.close();
    }
  }

  /**
   * Handle player close. Called from onClose callbacks in createVideoPlayer.
   */
  private handlePlayerClose(): void {
    this.emit('video:close');
    // Fire bridge event for post-close attribution (user clicked X)
    const currentVideo = this.campaign?.videos?.[this.currentVideoIndex];
    fireAnalyticsBridgeEvent('video_close', {
      videoId: currentVideo?.id || '',
    });
    this.storyDismissed = true;
    this.bubble?.hideEqualizer();
    this.currentVideoIndex = 0;
    this.show();
  }

  /**
   * Dismiss the widget for a period of time
   */
  dismiss(days?: number): void {
    this.trackEvent('dismiss', {});
    const dismissDays = days ?? this.campaign?.trigger.dismissDays ?? 7;
    this.dismissedUntil = Date.now() + dismissDays * 24 * 60 * 60 * 1000;
    this.saveDismissedState();
    this.hide();
    logger.debug(`Dismissed for ${dismissDays} days`);
  }

  /**
   * Destroy the SDK instance and clean up
   */
  destroy(): void {
    // Sprint 003 (2026-04-29): remove host-page listeners that haven't self-
    // removed yet. Without this, exit-intent and scroll-trigger handlers
    // leaked onto document/window forever in SPAs that destroy and re-init.
    if (this.exitIntentHandler) {
      document.removeEventListener('mousemove', this.exitIntentHandler);
      this.exitIntentHandler = null;
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    // Sprint 005 (2026-04-29): remove injected DNS-prefetch <link> tags so
    // SPA hosts don't accumulate duplicates across destroy/re-init cycles.
    for (const link of this.dnsPrefetchLinks) {
      link.remove();
    }
    this.dnsPrefetchLinks = [];
    // Sprint 005 (2026-04-29): unbind the module-level resize listener in
    // video-utils.ts. Module-level state persists across destroy, but the
    // listener should not.
    teardownVideoUtils();
    // Flush any remaining tracked events before cleanup
    this.flushEvents();
    this.bubble?.destroy();
    this.videoPlayer?.destroy();
    this.youtubePlayer?.destroy();
    this.instagramPlayer?.destroy();
    this.tiktokPlayer?.destroy();
    this.cloudflarePlayer?.destroy();
    this.overlayChoices?.destroy();
    destroyShadowDOM();
    this.eventHandlers.clear();
    this.config = null;
    this.campaign = null;
    this.bubble = null;
    this.videoPlayer = null;
    this.youtubePlayer = null;
    this.instagramPlayer = null;
    this.tiktokPlayer = null;
    this.overlayChoices = null;
    this.formOverlay = null;
    this.currentNodeId = null;
    this.flowNodes.clear();
    this.flowEdges = [];
    this.sessionId = null;
    this.state = 'uninitialized';
    logger.log('SDK destroyed');
  }

  // ===========================================================================
  // Private Methods - Initialization
  // ===========================================================================

  /**
   * Add DNS prefetch hints for video CDNs based on campaign video types.
   * Reduces latency on first video open by resolving DNS ahead of time.
   */
  private addVideoDnsPrefetch(): void {
    if (!this.campaign?.videos?.length) return;

    const hints = new Set<string>();

    for (const video of this.campaign.videos) {
      switch (video.videoType) {
        case 'youtube':
          hints.add('https://www.youtube.com');
          hints.add('https://i.ytimg.com');
          break;
        case 'instagram':
          hints.add('https://www.instagram.com');
          hints.add('https://scontent.cdninstagram.com');
          break;
        case 'tiktok':
          hints.add('https://www.tiktok.com');
          break;
        case 'cloudflare':
          hints.add('https://iframe.videodelivery.net');
          break;
      }
    }

    for (const href of hints) {
      const link = document.createElement('link');
      link.rel = 'dns-prefetch';
      link.href = href;
      document.head.appendChild(link);
      // Sprint 005 (2026-04-29): track for removal on destroy
      this.dnsPrefetchLinks.push(link);
    }
  }

  private validateDomain(): boolean {
    if (!this.config?.allowedDomains?.length) {
      return true; // No whitelist means allow all
    }

    const currentDomain = window.location.hostname;
    return this.config.allowedDomains.some((domain) => {
      // Support wildcards like *.example.com
      // M14 (2026-04-17): require a leading '.' before the base domain so
      // `*.example.com` matches `a.example.com` but NOT the apex `example.com`
      // and NOT `evilexample.com`. Previously `endsWith(baseDomain)` allowed
      // both bypasses.
      if (domain.startsWith('*.')) {
        const baseDomain = domain.slice(2);
        return currentDomain.endsWith('.' + baseDomain);
      }
      return currentDomain === domain;
    });
  }

  private async fetchCampaignConfig(): Promise<void> {
    if (!this.config) return;

    // Allow config-level showBranding override (used by preview/demo mode)
    if (this.config.showBranding !== undefined) {
      this.showBranding = this.config.showBranding;
    }

    // Demo mode: multi-video array for preview/story mode
    if (this.config.demoMode && this.config.videos && this.config.videos.length > 0) {
      logger.debug('Demo mode with multiple videos:', this.config.videos.length);
      const demoVideos = await Promise.all(
        this.config.videos.map(async (v, i) => {
          const youtubeId = this.extractYouTubeId(v.url);
          const tiktokId = this.extractTikTokId(v.url);
          const isInstagram = this.isInstagramUrl(v.url);
          let thumbnailUrl = '';
          if (youtubeId) {
            thumbnailUrl = `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
          } else if (tiktokId) {
            thumbnailUrl = await this.fetchTikTokThumbnail(v.url);
          }
          return {
            id: v.id || `demo-video-${i}`,
            url: youtubeId ? '' : v.url,
            youtubeId,
            tiktokId,
            instagramPostUrl: isInstagram ? v.url : undefined,
            instagramVideoUrl:
              isInstagram && v.playerMode !== 'embed' ? v.url : undefined,
            thumbnailUrl,
            title: `Video ${i + 1}`,
            duration: 0,
            ctaText: v.ctaText || this.config!.ctaText || 'Learn More',
            ctaUrl: v.ctaUrl || this.config!.ctaUrl || 'https://example.com',
            orientation: 'portrait' as const,
          };
        }),
      );
      this.campaign = {
        id: this.config.campaignId || 'demo-multi-campaign',
        name: 'Multi-Video Demo Campaign',
        videos: demoVideos,
        targeting: {
          urls: ['/*'],
          devices: ['desktop', 'mobile', 'tablet'],
        },
        styling: {
          primaryColor: this.config.primaryColor || '#6366f1',
          position: this.config.position,
          bubbleSize: this.config.bubbleSize || 'medium',
          bubbleShape: this.config.bubbleShape || 'circle',
          entranceAnimation: this.config.entranceAnimation || 'fade',
          shakeIntensity: this.config.shakeIntensity || undefined,
        },
        trigger: {
          type: 'delay',
          delaySeconds: 2,
          dismissDays: 7,
        },
      };
      this.sessionId = 'demo-' + Math.random().toString(36).substring(2, 15);
      logger.debug('Multi-video demo campaign loaded:', this.campaign);
      return;
    }

    // Demo mode: use local configuration with Instagram video (priority over YouTube)
    if (this.config.demoMode && (this.config.instagramVideoUrl || this.config.instagramPostUrl)) {
      const mode = this.config.instagramVideoUrl ? 'MP4' : 'embed';
      logger.debug(`Demo mode enabled with Instagram video (${mode}):`,
        this.config.instagramVideoUrl || this.config.instagramPostUrl);
      this.campaign = {
        id: this.config.campaignId || 'demo-instagram-campaign',
        name: 'Instagram Demo Campaign',
        videos: [
          {
            id: this.config.videoId || 'instagram-demo-video',
            url: this.config.instagramVideoUrl || '', // MP4 URL if provided
            instagramVideoUrl: this.config.instagramVideoUrl,
            instagramPostUrl: this.config.instagramPostUrl,
            thumbnailUrl: '', // Instagram embeds handle their own thumbnails
            videoPreviewUrl: this.config.videoPreviewUrl,
            title: 'Demo Video',
            duration: 0,
            ctaText: this.config.ctaText || 'Learn More',
            ctaUrl: this.config.ctaUrl || 'https://example.com',
            orientation: 'portrait' as const,
          },
        ],
        targeting: {
          urls: ['/*'],
          devices: ['desktop', 'mobile', 'tablet'],
        },
        styling: {
          primaryColor: this.config.primaryColor || '#E1306C', // Instagram brand color
          position: this.config.position,
          bubbleSize: this.config.bubbleSize || 'medium',
          bubbleShape: this.config.bubbleShape || 'circle',
          entranceAnimation: this.config.entranceAnimation || 'fade',
          shakeIntensity: this.config.shakeIntensity || undefined,
        },
        trigger: {
          type: 'delay',
          delaySeconds: 2,
          dismissDays: 7,
        },
      };
      this.sessionId = 'demo-' + Math.random().toString(36).substring(2, 15);
      logger.debug('Instagram demo campaign config loaded:', this.campaign);
      return;
    }

    // Demo mode: use local configuration with TikTok video (priority over YouTube)
    if (this.config.demoMode && this.config.tiktokVideoUrl) {
      const tiktokId = this.extractTikTokId(this.config.tiktokVideoUrl);
      logger.debug('Demo mode enabled with TikTok video:', this.config.tiktokVideoUrl);
      const tiktokThumbnail = await this.fetchTikTokThumbnail(this.config.tiktokVideoUrl);
      this.campaign = {
        id: this.config.campaignId || 'demo-tiktok-campaign',
        name: 'TikTok Demo Campaign',
        videos: [
          {
            id: this.config.videoId || 'tiktok-demo-video',
            url: this.config.tiktokVideoUrl,
            tiktokId: tiktokId,
            thumbnailUrl: tiktokThumbnail,
            videoPreviewUrl: this.config.videoPreviewUrl,
            title: 'Demo Video',
            duration: 0,
            ctaText: this.config.ctaText || 'Learn More',
            ctaUrl: this.config.ctaUrl || 'https://example.com',
            orientation: 'portrait' as const,
          },
        ],
        targeting: {
          urls: ['/*'],
          devices: ['desktop', 'mobile', 'tablet'],
        },
        styling: {
          primaryColor: this.config.primaryColor || '#fe2c55', // TikTok brand color
          position: this.config.position,
          bubbleSize: this.config.bubbleSize || 'medium',
          bubbleShape: this.config.bubbleShape || 'circle',
          entranceAnimation: this.config.entranceAnimation || 'fade',
          shakeIntensity: this.config.shakeIntensity || undefined,
        },
        trigger: {
          type: 'delay',
          delaySeconds: 2,
          dismissDays: 7,
        },
      };
      this.sessionId = 'demo-' + Math.random().toString(36).substring(2, 15);
      logger.debug('TikTok demo campaign config loaded:', this.campaign);
      return;
    }

    // Demo mode: use local configuration with YouTube video
    if (this.config.demoMode && this.config.youtubeVideoId) {
      logger.debug('Demo mode enabled with YouTube video:', this.config.youtubeVideoId);
      this.campaign = {
        id: this.config.campaignId || 'demo-youtube-campaign',
        name: 'YouTube Demo Campaign',
        videos: [
          {
            id: this.config.videoId || 'youtube-demo-video',
            url: '', // Empty for YouTube videos
            youtubeId: this.config.youtubeVideoId,
            thumbnailUrl: `https://img.youtube.com/vi/${this.config.youtubeVideoId}/maxresdefault.jpg`,
            videoPreviewUrl: this.config.videoPreviewUrl,
            title: 'Demo Video',
            duration: 0, // Unknown for YouTube
            ctaText: this.config.ctaText || 'Learn More',
            ctaUrl: this.config.ctaUrl || 'https://example.com',
            orientation: 'portrait' as const,
          },
        ],
        targeting: {
          urls: ['/*'],
          devices: ['desktop', 'mobile', 'tablet'],
        },
        styling: {
          primaryColor: this.config.primaryColor || '#6366f1',
          position: this.config.position,
          bubbleSize: this.config.bubbleSize || 'medium',
          bubbleShape: this.config.bubbleShape || 'circle',
          entranceAnimation: this.config.entranceAnimation || 'fade',
          shakeIntensity: this.config.shakeIntensity || undefined,
        },
        trigger: {
          type: 'delay',
          delaySeconds: 2,
          dismissDays: 7,
        },
      };
      this.sessionId = 'demo-' + Math.random().toString(36).substring(2, 15);
      logger.debug('Demo campaign config loaded:', this.campaign);
      return;
    }

    let url = `${this.config.apiUrl}/v1/sdk/init?siteKey=${encodeURIComponent(this.config.siteKey)}`;
    if (this.config.campaignId) {
      url += `&campaignId=${encodeURIComponent(this.config.campaignId)}`;
    }
    // Send current page path so the server can select the best-matching campaign
    try {
      url += `&url=${encodeURIComponent(window.location.pathname)}`;
    } catch {
      // Ignore if pathname not available (e.g., in SSR)
    }
    logger.debug('Fetching campaign config from:', url);

    // M13 (2026-04-17): 3 total attempts with exponential backoff (1s, 2s).
    // Previously the loop ran `<=` MAX_RETRIES which produced 4 attempts —
    // contradicting the "3 retries" wording in docs and inflating startup
    // latency on down-servers.
    const MAX_RETRIES = 3;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          // Sprint 003 (2026-04-29): mark 4xx as permanent so the catch block
          // skips retries. Previously a 404 (invalid siteKey) or 403 (gated
          // account) was retried 3× over 3s of total backoff — wasted startup
          // latency on errors that will never recover.
          const error = new Error(`API returned ${response.status}`) as Error & { permanent?: boolean };
          if (response.status >= 400 && response.status < 500) {
            error.permanent = true;
          }
          throw error;
        }
        const data = await response.json();

        // Store session ID for event tracking
        this.sessionId = data.sessionId || null;
        logger.debug('Session ID:', this.sessionId);

        // Store branding settings from server
        if (data.settings) {
          this.showBranding = data.settings.showBranding ?? true;
        }

        if (!data.campaign) {
          logger.warn('No active campaign returned from API');
          return;
        }

        const apiCampaign = data.campaign;

        // Map API response to SDK campaign config
        this.campaign = {
          id: apiCampaign.id,
          name: apiCampaign.name,
          videos: (apiCampaign.videos || []).map((v: Record<string, unknown>) => {
            const videoUrl = (v.url as string) || '';
            return {
              id: v.id as string,
              url: videoUrl,
              youtubeId: this.extractYouTubeId(videoUrl),
              tiktokId: this.extractTikTokId(videoUrl),
              instagramPostUrl: this.isInstagramUrl(videoUrl) ? videoUrl : undefined,
              instagramVideoUrl: this.isInstagramUrl(videoUrl) && (v.playerMode as string) !== 'embed' ? videoUrl : undefined,
              thumbnailUrl: (v.thumbnailUrl as string) || this.getYouTubeThumbnail(videoUrl),
              title: (v.title as string) || apiCampaign.name,
              duration: (v.duration as number) || 0,
              ctaText: (v.ctaText as string) || 'Learn More',
              ctaUrl: (v.ctaUrl as string) || '',
              orientation: (v.orientation as 'portrait' | 'landscape' | 'square') || 'portrait',
              cloudflareStreamUid: (v.cloudflareStreamUid as string) || undefined,
              cloudflareStreamToken: (v.cloudflareStreamToken as string) || undefined,
            };
          }),
          targeting: {
            urls: apiCampaign.targeting?.urls || ['/*'],
            devices: apiCampaign.targeting?.devices || ['desktop', 'mobile', 'tablet'],
          },
          styling: {
            primaryColor: this.config.primaryColor || apiCampaign.styling?.primaryColor || '#6366f1',
            position: this.config.position || apiCampaign.styling?.position || 'bottom-right',
            bubbleSize: this.config.bubbleSize || apiCampaign.styling?.bubbleSize || 'medium',
            bubbleShape: this.config.bubbleShape || apiCampaign.styling?.bubbleShape || 'circle',
            entranceAnimation: this.config.entranceAnimation || apiCampaign.styling?.entranceAnimation || 'fade',
            morphStyle: apiCampaign.styling?.morphStyle || undefined,
            morphEffects: apiCampaign.styling?.morphEffects || undefined,
            shakeIntensity: apiCampaign.styling?.shakeIntensity || undefined,
          },
          trigger: {
            type: apiCampaign.trigger?.type || 'delay',
            delaySeconds: apiCampaign.trigger?.delaySeconds ?? 2,
            dismissDays: apiCampaign.trigger?.dismissDays ?? 7,
          },
        };
        // Store interactive flow graph if present
        if (apiCampaign.type === 'interactive' && apiCampaign.flow) {
          this.campaign.type = 'interactive';
          this.campaign.flow = apiCampaign.flow;
          this.flowNodes.clear();
          this.flowEdges = apiCampaign.flow.edges || [];
          for (const node of apiCampaign.flow.nodes || []) {
            // Map video config with platform-specific IDs
            let mappedVideo = node.video;
            if (node.video) {
              const videoUrl = (node.video.url as string) || '';
              mappedVideo = {
                ...node.video,
                youtubeId: this.extractYouTubeId(videoUrl),
                tiktokId: this.extractTikTokId(videoUrl),
                instagramPostUrl: this.isInstagramUrl(videoUrl) ? videoUrl : undefined,
                instagramVideoUrl: this.isInstagramUrl(videoUrl) ? videoUrl : undefined,
                orientation: node.video.orientation || 'portrait',
              };
            }
            this.flowNodes.set(node.id, { ...node, video: mappedVideo });
          }
          logger.debug('Interactive flow graph loaded:', this.flowNodes.size, 'nodes,', this.flowEdges.length, 'edges');
        }

        logger.debug('Campaign config loaded from API:', this.campaign);
        return; // Success, exit retry loop
      } catch (error) {
        lastError = error as Error;
        logger.warn(`Init attempt ${attempt + 1}/${MAX_RETRIES} failed:`, error);
        // Sprint 003 (2026-04-29): permanent errors (4xx) skip the backoff loop
        // and break out immediately. Only 5xx and network errors are retried.
        if ((error as Error & { permanent?: boolean }).permanent) {
          logger.warn('Permanent error — skipping retry');
          break;
        }
        if (attempt < MAX_RETRIES - 1) {
          await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
        }
      }
    }
    // All retries exhausted (or permanent error)
    logger.error('Failed to fetch campaign config after retries:', lastError);
    throw new Error('Failed to load campaign configuration');
  }

  private matchesTargeting(): boolean {
    if (!this.campaign?.targeting) return true;

    const { urls, devices } = this.campaign.targeting;

    // Check URL matching
    if (urls?.length) {
      const currentPath = window.location.pathname;
      const matches = urls.some((pattern) => {
        const regex = new RegExp(
          '^' + pattern.replace(/\*/g, '.*').replace(/\//g, '\\/') + '$'
        );
        return regex.test(currentPath);
      });
      if (!matches) return false;
    }

    // Check device matching
    if (devices?.length) {
      const currentDevice = this.detectDevice();
      if (!devices.includes(currentDevice)) return false;
    }

    return true;
  }

  private detectDevice(): 'desktop' | 'mobile' | 'tablet' {
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  // ===========================================================================
  // Private Methods - UI Components
  // ===========================================================================

  private createBubble(): void {
    if (!this.campaign) return;

    const position = this.campaign.styling.position || this.config?.position || 'bottom-right';
    const video = this.campaign.videos[0];

    this.bubble = new BubbleTrigger({
      position,
      styling: this.campaign.styling,
      video,
      socialProof: this.config?.socialProof,
      showBranding: this.showBranding,
      onClick: () => {
        this.emit('bubble:click');
        this.trackEvent('bubble_click', {});
        this.openVideo();
      },
      onHover: () => {
        logger.debug('Bubble hovered');
        if (!this.hoverTracked) {
          this.hoverTracked = true;
          this.trackEvent('bubble_hover', {});
        }
      },
    });

    this.bubble.render();
    this.bubble.show();
    this.bubble.startPreloading();

    // Fire bubble_impression once per SDK lifecycle
    if (!this.impressionFired) {
      this.impressionFired = true;
      this.trackEvent('bubble_impression', {
        campaignId: this.campaign.id,
        position,
      });
    }
  }

  private createVideoPlayer(): void {
    if (!this.campaign?.videos?.[this.currentVideoIndex]) return;

    const video = this.campaign.videos[this.currentVideoIndex];
    const position = this.campaign.styling.position || this.config?.position || 'bottom-right';
    const storyMode = (this.campaign?.videos?.length ?? 0) > 1;
    const storyIndex = this.currentVideoIndex;
    const storyTotal = this.campaign?.videos?.length ?? 1;

    // Use Cloudflare Stream player if video has a signed token (highest priority)
    if (video.cloudflareStreamToken) {
      this.cloudflarePlayer = new CloudflarePlayer({
        signedToken: video.cloudflareStreamToken,
        videoUid: video.cloudflareStreamUid,
        estimatedDuration: video.duration || 300,
        position,
        styling: this.campaign.styling,
        ctaText: video.ctaText,
        ctaUrl: video.ctaUrl,
        campaignName: this.campaign.name,
        sessionId: this.sessionId || undefined,
        videoIdForTracking: video.id,
        floatingActions: this.config?.floatingActions,
        muteOnOpen: this.config?.muteOnOpen ?? false,
        productCard: this.config?.productCard,
        socialProof: this.config?.socialProof,
        showBranding: this.showBranding,
        storyMode,
        storyIndex,
        storyTotal,
        onStoryAdvance: (direction: 'next' | 'prev') => this.advanceStory(direction),
        onPlay: () => {
          this.emit('video:play');
          this.bubble?.showEqualizer();
          this.trackEvent('video_play', { videoId: video.id, source: 'cloudflare' });
        },
        onPause: () => {
          this.emit('video:pause');
          this.trackEvent('video_pause', { videoId: video.id, source: 'cloudflare' });
        },
        onProgress: (data: VideoProgressData) => {
          this.checkOverlayTrigger(data.percent);
          this.emit('video:progress', data);
          if (data.quartile > 0) {
            this.trackEvent('video_progress', {
              videoId: video.id,
              source: 'cloudflare',
              quartile: data.quartile,
              currentTime: data.currentTime,
              duration: data.duration,
            });
          }
        },
        onComplete: () => {
          this.emit('video:complete');
          this.trackEvent('video_complete', { videoId: video.id, source: 'cloudflare' });
          if (!storyMode && !this.currentNodeId) {
            setTimeout(() => {
              this.closeVideo();
              this.bubble?.hideEqualizer();
              this.show();
            }, 1500);
          }
        },
        onClose: () => this.handlePlayerClose(),
        onCtaClick: () => {
          this.emit('cta:click');
          this.trackEvent('cta_click', { videoId: video.id, ctaUrl: video.ctaUrl });
        },
        onFloatingActionClick: (action) => {
          this.trackEvent('floating_action_click', { videoId: video.id, actionId: action.id, actionUrl: action.url });
        },
      });
      return;
    }

    // Use Instagram player if video has Instagram config (priority over YouTube)
    if (video.instagramVideoUrl || video.instagramPostUrl) {
      this.instagramPlayer = new InstagramPlayer({
        videoUrl: video.instagramVideoUrl,
        postUrl: video.instagramPostUrl,
        estimatedDuration: video.duration || 300,
        position,
        styling: this.campaign.styling,
        ctaText: video.ctaText,
        ctaUrl: video.ctaUrl,
        campaignName: this.campaign.name,
        sessionId: this.sessionId || undefined,
        videoIdForTracking: video.id,
        floatingActions: this.config?.floatingActions,
        muteOnOpen: this.config?.muteOnOpen ?? false,
        productCard: this.config?.productCard,
        socialProof: this.config?.socialProof,
        showBranding: this.showBranding,
        storyMode,
        storyIndex,
        storyTotal,
        onStoryAdvance: (direction: 'next' | 'prev') => this.advanceStory(direction),
        onPlay: () => {
          this.emit('video:play');
          this.bubble?.showEqualizer();
          this.trackEvent('video_play', { videoId: video.id, source: 'instagram' });
        },
        onPause: () => {
          this.emit('video:pause');
          this.trackEvent('video_pause', { videoId: video.id, source: 'instagram' });
        },
        onProgress: (data: VideoProgressData) => {
          this.checkOverlayTrigger(data.percent);
          this.emit('video:progress', data);
          if (data.quartile > 0) {
            this.trackEvent('video_progress', {
              videoId: video.id,
              source: 'instagram',
              quartile: data.quartile,
              currentTime: data.currentTime,
              duration: data.duration,
            });
          }
        },
        onComplete: () => {
          this.emit('video:complete');
          this.trackEvent('video_complete', { videoId: video.id, source: 'instagram' });
          if (!storyMode && !this.currentNodeId) {
            setTimeout(() => {
              this.closeVideo();
              this.bubble?.hideEqualizer();
              this.show();
            }, 1500);
          }
        },
        onClose: () => this.handlePlayerClose(),
        onCtaClick: () => {
          this.emit('cta:click');
          this.trackEvent('cta_click', { videoId: video.id, ctaUrl: video.ctaUrl });
        },
        onFloatingActionClick: (action) => {
          this.trackEvent('floating_action_click', { videoId: video.id, actionId: action.id, actionUrl: action.url });
        },
      });
      return;
    }

    // Use TikTok player if video has a tiktokId
    if (video.tiktokId) {
      this.tiktokPlayer = new TikTokPlayer({
        videoId: video.tiktokId,
        videoUrl: video.url || undefined,
        apiUrl: this.config?.apiUrl,
        estimatedDuration: video.duration || 300,
        position,
        styling: this.campaign.styling,
        ctaText: video.ctaText,
        ctaUrl: video.ctaUrl,
        campaignName: this.campaign.name,
        sessionId: this.sessionId || undefined,
        videoIdForTracking: video.id,
        floatingActions: this.config?.floatingActions,
        muteOnOpen: this.config?.muteOnOpen ?? false,
        productCard: this.config?.productCard,
        socialProof: this.config?.socialProof,
        showBranding: this.showBranding,
        storyMode,
        storyIndex,
        storyTotal,
        onStoryAdvance: (direction: 'next' | 'prev') => this.advanceStory(direction),
        onPlay: () => {
          this.emit('video:play');
          this.bubble?.showEqualizer();
          this.trackEvent('video_play', { videoId: video.id, tiktokId: video.tiktokId });
        },
        onPause: () => {
          this.emit('video:pause');
          this.trackEvent('video_pause', { videoId: video.id, tiktokId: video.tiktokId });
        },
        onProgress: (data: VideoProgressData) => {
          this.checkOverlayTrigger(data.percent);
          this.emit('video:progress', data);
          if (data.quartile > 0) {
            this.trackEvent('video_progress', {
              videoId: video.id,
              tiktokId: video.tiktokId,
              quartile: data.quartile,
              currentTime: data.currentTime,
              duration: data.duration,
            });
          }
        },
        onComplete: () => {
          this.emit('video:complete');
          this.trackEvent('video_complete', { videoId: video.id, tiktokId: video.tiktokId });
          if (!storyMode && !this.currentNodeId) {
            setTimeout(() => {
              this.closeVideo();
              this.bubble?.hideEqualizer();
              this.show();
            }, 1500);
          }
        },
        onClose: () => this.handlePlayerClose(),
        onCtaClick: () => {
          this.emit('cta:click');
          this.trackEvent('cta_click', { videoId: video.id, ctaUrl: video.ctaUrl });
        },
        onFloatingActionClick: (action) => {
          this.trackEvent('floating_action_click', { videoId: video.id, actionId: action.id, actionUrl: action.url });
        },
      });
      return;
    }

    // Use YouTube player if video has a youtubeId
    if (video.youtubeId) {
      this.youtubePlayer = new YouTubePlayer({
        videoId: video.youtubeId,
        position,
        styling: this.campaign.styling,
        ctaText: video.ctaText,
        ctaUrl: video.ctaUrl,
        campaignName: this.campaign.name,
        sessionId: this.sessionId || undefined,
        videoIdForTracking: video.id,
        floatingActions: this.config?.floatingActions,
        muteOnOpen: this.config?.muteOnOpen ?? false,
        productCard: this.config?.productCard,
        socialProof: this.config?.socialProof,
        showBranding: this.showBranding,
        storyMode,
        storyIndex,
        storyTotal,
        onStoryAdvance: (direction: 'next' | 'prev') => this.advanceStory(direction),
        onPlay: () => {
          this.emit('video:play');
          this.bubble?.showEqualizer();
          this.trackEvent('video_play', { videoId: video.id, youtubeId: video.youtubeId });
        },
        onPause: () => {
          this.emit('video:pause');
          this.trackEvent('video_pause', { videoId: video.id, youtubeId: video.youtubeId });
        },
        onProgress: (data: VideoProgressData) => {
          this.checkOverlayTrigger(data.percent);
          this.emit('video:progress', data);
          if (data.quartile > 0) {
            this.trackEvent('video_progress', {
              videoId: video.id,
              youtubeId: video.youtubeId,
              quartile: data.quartile,
              currentTime: data.currentTime,
              duration: data.duration,
            });
          }
        },
        onComplete: () => {
          this.emit('video:complete');
          this.trackEvent('video_complete', { videoId: video.id, youtubeId: video.youtubeId });
          if (!storyMode && !this.currentNodeId) {
            setTimeout(() => {
              this.closeVideo();
              this.bubble?.hideEqualizer();
              this.show();
            }, 1500);
          }
        },
        onClose: () => this.handlePlayerClose(),
        onCtaClick: () => {
          this.emit('cta:click');
          this.trackEvent('cta_click', { videoId: video.id, ctaUrl: video.ctaUrl });
        },
        onFloatingActionClick: (action) => {
          this.trackEvent('floating_action_click', { videoId: video.id, actionId: action.id, actionUrl: action.url });
        },
      });
      return;
    }

    // Fall back to standard video player
    this.videoPlayer = new VideoPlayer({
      video,
      position,
      styling: this.campaign.styling,
      campaignName: this.campaign.name,
      sessionId: this.sessionId || undefined,
      muteOnOpen: this.config?.muteOnOpen ?? false,
      productCard: this.config?.productCard,
      socialProof: this.config?.socialProof,
      showBranding: this.showBranding,
      storyMode,
      storyIndex,
      storyTotal,
      onStoryAdvance: (direction: 'next' | 'prev') => this.advanceStory(direction),
      onPlay: () => {
        this.emit('video:play');
        this.bubble?.showEqualizer();
        this.trackEvent('video_play', { videoId: video.id });
      },
      onPause: () => {
        this.emit('video:pause');
        this.trackEvent('video_pause', { videoId: video.id });
      },
      onProgress: (data: VideoProgressData) => {
        this.checkOverlayTrigger(data.percent);
        this.emit('video:progress', data);
        if (data.quartile > 0) {
          this.trackEvent('video_progress', {
            videoId: video.id,
            quartile: data.quartile,
            currentTime: data.currentTime,
            duration: data.duration,
          });
        }
      },
      onComplete: () => {
        this.emit('video:complete');
        this.trackEvent('video_complete', { videoId: video.id });
        if (!storyMode && !this.currentNodeId) {
          setTimeout(() => {
            this.closeVideo();
            this.bubble?.hideEqualizer();
            this.show();
          }, 1500);
        }
      },
      onClose: () => this.handlePlayerClose(),
      onCtaClick: () => {
        this.emit('cta:click');
        this.trackEvent('cta_click', { videoId: video.id, ctaUrl: video.ctaUrl });
      },
    });
  }

  // ===========================================================================
  // Private Methods - Story Mode, Exit Intent, Scroll Trigger
  // ===========================================================================

  private advanceStory(direction: 'next' | 'prev'): void {
    if (this.storyDismissed) return; // User explicitly closed — stop story
    if (!this.campaign?.videos || this.campaign.videos.length <= 1) return;
    const total = this.campaign.videos.length;
    if (direction === 'next') {
      if (this.currentVideoIndex >= total - 1) {
        // Fire story_complete before resetting index
        this.trackEvent('story_complete', {
          videoId: this.campaign!.videos[this.currentVideoIndex].id,
        });
        // Last video finished — close the player and show the bubble
        this.closeVideo();
        this.bubble?.hideEqualizer();
        this.show();
        this.currentVideoIndex = 0;
        return;
      }
      this.currentVideoIndex++;
    } else {
      this.currentVideoIndex = (this.currentVideoIndex - 1 + total) % total;
    }

    // Fire story_advance after index update
    this.trackEvent('story_advance', {
      videoId: this.campaign!.videos[this.currentVideoIndex].id,
      direction,
    });
    // Destroy current players
    this.videoPlayer?.destroy();
    this.youtubePlayer?.destroy();
    this.instagramPlayer?.destroy();
    this.tiktokPlayer?.destroy();
    this.cloudflarePlayer?.destroy();
    this.videoPlayer = null;
    this.youtubePlayer = null;
    this.instagramPlayer = null;
    this.tiktokPlayer = null;
    this.cloudflarePlayer = null;
    // Create new player for the new video
    this.createVideoPlayer();
    // Open the new player
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    // H6 (2026-04-17): cloudflarePlayer was missing from the re-open chain,
    // causing multi-video stories with a Cloudflare Stream entry to go blank
    // when advancing onto it. Priority matches createVideoPlayer() selection.
    if (self.cloudflarePlayer) self.cloudflarePlayer.open();
    else if (self.instagramPlayer) self.instagramPlayer.open();
    else if (self.tiktokPlayer) self.tiktokPlayer.open();
    else if (self.youtubePlayer) self.youtubePlayer.open();
    else self.videoPlayer?.open();
  }

  // ===========================================================================
  // Interactive Campaign Navigation
  // ===========================================================================

  private navigateToNode(nodeId: string): void {
    const node = this.flowNodes.get(nodeId);
    if (!node) {
      logger.warn('Node not found in flow graph:', nodeId);
      return;
    }

    this.currentNodeId = nodeId;
    this.overlayTriggered = false;

    // Destroy current players and overlays
    this.videoPlayer?.destroy();
    this.youtubePlayer?.destroy();
    this.instagramPlayer?.destroy();
    this.tiktokPlayer?.destroy();
    this.cloudflarePlayer?.destroy();
    this.overlayChoices?.destroy();
    this.formOverlay?.destroy();
    // Remove overlay wrapper from Shadow DOM
    const shadowRoot = getShadowContainer();
    if (shadowRoot) {
      const wrapper = shadowRoot.querySelector('.clickreel-overlay-choices-wrapper');
      wrapper?.remove();
    }
    this.videoPlayer = null;
    this.youtubePlayer = null;
    this.instagramPlayer = null;
    this.tiktokPlayer = null;
    this.cloudflarePlayer = null;
    this.overlayChoices = null;
    this.formOverlay = null;

    logger.debug('Navigating to node:', nodeId, node.nodeType, node.label);

    // G1 (2026-04-17): defensive fallback — if a video-type node has no
    // `video` object (DB state where campaign_nodes.videoId is null), try
    // the campaign's primary video. Without this, the SDK would hide the
    // bubble and produce a blank preview. Ingest also applies this fallback
    // at serialization time; this is belt-and-braces.
    let videoForNode = node.video;
    if (node.nodeType === 'video' && !videoForNode) {
      const fallback = this.campaign?.videos?.[this.currentVideoIndex] ?? this.campaign?.videos?.[0];
      if (fallback) {
        logger.warn(
          'Video-type node has no video linked; falling back to campaign.videos[0]',
          nodeId,
        );
        videoForNode = fallback;
      }
    }

    if (node.nodeType === 'video' && videoForNode) {
      // Set the video in the campaign's videos array so createVideoPlayer works
      this.campaign!.videos = [videoForNode];
      this.currentVideoIndex = 0;

      // Create the player
      this.createVideoPlayer();

      // Open the player FIRST (creates the DOM container)
      // Await async players (YouTube, Cloudflare) to ensure DOM exists before overlay setup
      // D3 (2026-04-17): wrapped in a fresh-scope arrow so TS does not
      // carry the `= null` narrowing from the destroy-and-reset block above.
      // `createVideoPlayer()` mutates the fields but TS treats method calls
      // as opaque — inside a new closure, reads start from the declared type.
      const openNextPlayer = (): Promise<void> | void => {
        if (this.cloudflarePlayer) return this.cloudflarePlayer.open();
        if (this.instagramPlayer) return this.instagramPlayer.open();
        if (this.tiktokPlayer) return this.tiktokPlayer.open();
        if (this.youtubePlayer) return this.youtubePlayer.open();
        return this.videoPlayer?.open();
      };
      const openPromise: Promise<void> | void = openNextPlayer();

      // Set up overlay choices AFTER the player is fully open
      if (node.overlay && (node.overlay as OverlayChoicesConfig).type === 'choices') {
        const setupOverlay = () => {
          logger.debug('Setting up overlay choices for node:', node.id);
          this.setupOverlayChoicesForNode(node);
        };
        // If player open returns a promise (YouTube, Cloudflare), await it then setup
        if (openPromise && typeof (openPromise as Promise<void>).then === 'function') {
          (openPromise as Promise<void>).then(() => setTimeout(setupOverlay, 500)).catch(() => setTimeout(setupOverlay, 1000));
        } else {
          // Synchronous players (HTML5, Instagram, TikTok) — short delay is enough
          setTimeout(setupOverlay, 300);
        }
      }
    } else if (node.nodeType === 'form') {
      // Show form overlay directly (no video player needed)
      this.setupFormOverlayForNode(node);
    } else if (node.nodeType === 'call') {
      // Show click-to-call overlay
      this.setupCallNodeOverlay(node);
    } else if (node.nodeType === 'end_screen') {
      // Close everything and show bubble again
      this.closeVideo();
      this.show();
    }
  }

  private setupOverlayChoicesForNode(node: InteractionNode): void {
    if (!node.overlay || (node.overlay as OverlayChoicesConfig).type !== 'choices') return;

    const overlayConfig = node.overlay as OverlayChoicesConfig;
    // Find edges that start from this node
    const nodeEdges = this.flowEdges.filter((e) => e.sourceNodeId === node.id);

    // Use Shadow DOM container — reliable since it always exists after init
    const shadowContainer = getShadowContainer();
    if (!shadowContainer) {
      logger.warn('No Shadow DOM container for overlay choices');
      return;
    }

    // Create a fixed-position wrapper that overlays the video player
    // On mobile: full width (video is full-screen)
    // On desktop: constrained to PiP player width (380px) and positioned to match
    const overlayWrapper = document.createElement('div');
    overlayWrapper.className = 'clickreel-overlay-choices-wrapper';
    const mobile = isMobile();
    const position = this.campaign?.styling.position || 'bottom-right';
    if (mobile) {
      overlayWrapper.style.cssText = 'position:fixed;bottom:0;left:0;right:0;z-index:2147483646;pointer-events:none;';
    } else {
      // Desktop PiP: match the 380px player width and position
      const isRight = position === 'bottom-right' || position === 'top-right';
      const isBottom = position === 'bottom-right' || position === 'bottom-left';
      const hPos = isRight ? 'right:24px;' : 'left:24px;';
      const vPos = isBottom ? 'bottom:24px;' : 'top:24px;';
      overlayWrapper.style.cssText = `position:fixed;${vPos}${hPos}width:380px;z-index:2147483646;pointer-events:none;`;
    }
    shadowContainer.appendChild(overlayWrapper);

    this.overlayChoices = new OverlayChoices({
      config: overlayConfig,
      edges: nodeEdges,
      primaryColor: this.campaign?.styling.primaryColor || '#6366f1',
      onChoiceSelected: (edgeId: string, choiceLabel: string) => {
        // Remove wrapper when choice is made
        overlayWrapper.remove();
        this.handleChoiceSelected(edgeId, choiceLabel);
      },
    });

    this.overlayChoices.render(overlayWrapper);
    logger.debug('Overlay choices rendered, waiting for trigger at', overlayConfig.triggerAtPercent + '%');
  }

  private setupFormOverlayForNode(node: InteractionNode): void {
    const formConfig = node.overlay as import('./types').FormOverlayConfig;
    if (!formConfig || formConfig.type !== 'form') return;
    if (!this.config || !this.campaign || !this.sessionId) return;

    // For form-only nodes, we need a container. Use the Shadow DOM root.
    const container = getShadowContainer();
    if (!container) {
      logger.warn('No Shadow DOM container for form overlay');
      return;
    }

    // J1 (2026-04-19): form container now mirrors the video-player PiP
    // geometry on desktop (380px at the configured corner) instead of filling
    // the viewport. On mobile, keeps the previous fullscreen bottom-sheet
    // style. Matches the video player's footprint so the interactive flow is
    // visually consistent across video → form transitions.
    const mobile = isMobile();
    const cornerPos = (this.campaign?.styling?.position as string) || 'bottom-right';
    const margin = '24px';
    const cornerStyle =
      cornerPos === 'bottom-left' ? `bottom:${margin};left:${margin};` :
      cornerPos === 'top-right' ? `top:${margin};right:${margin};` :
      cornerPos === 'top-left' ? `top:${margin};left:${margin};` :
      `bottom:${margin};right:${margin};`;

    const backdrop = document.createElement('div');
    backdrop.className = 'clickreel-form-backdrop';
    backdrop.style.cssText = mobile
      ? 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:100;display:flex;align-items:flex-end;justify-content:center;pointer-events:auto;'
      : 'position:fixed;inset:0;background:rgba(0,0,0,0.50);z-index:100;pointer-events:auto;';
    container.appendChild(backdrop);

    const formWrapper = document.createElement('div');
    // Relative positioning on desktop so the form-overlay's transform
    // animation anchors to the wrapper, not the full-viewport backdrop.
    formWrapper.style.cssText = mobile
      ? 'width:100%;max-width:100vw;position:relative;'
      : `position:absolute;width:380px;max-width:90vw;${cornerStyle}`;
    backdrop.appendChild(formWrapper);

    this.formOverlay = new FormOverlay({
      config: formConfig,
      primaryColor: this.campaign.styling.primaryColor || '#6366f1',
      siteKey: this.config.siteKey,
      sessionId: this.sessionId,
      campaignId: this.campaign.id,
      nodeId: node.id,
      apiUrl: this.config.apiUrl || DEFAULT_API_URL,
      onFormSubmitted: (responseId: string) => {
        this.trackEvent('form_submitted', { nodeId: node.id, responseId });
        this.emit('form:submit', { responseId });
        // Navigate to next node if edge exists
        const nextEdge = this.flowEdges.find((e) => e.sourceNodeId === node.id);
        setTimeout(() => {
          backdrop.remove();
          if (nextEdge?.targetNodeId) {
            this.navigateToNode(nextEdge.targetNodeId);
          } else {
            this.closeVideo();
            this.show();
          }
        }, 3500); // Wait for success message to display
      },
      onFieldInteraction: (fieldName: string) => {
        this.trackEvent('form_field_interaction', { nodeId: node.id, fieldName });
        this.emit('form:field', { fieldName });
      },
    });

    this.formOverlay.render(formWrapper);
    this.trackEvent('form_shown', { nodeId: node.id });
    this.emit('form:show', { nodeId: node.id });

    // Show immediately for form-only nodes
    requestAnimationFrame(() => {
      this.formOverlay?.show();
    });
  }

  private setupCallNodeOverlay(node: InteractionNode): void {
    const callConfig = node.callConfig || (node.overlay as unknown as { phoneNumber?: string });
    const phoneNumber = callConfig?.phoneNumber || '';
    if (!phoneNumber) {
      logger.warn('Call node has no phone number configured');
      return;
    }

    const shadowContainer = getShadowContainer();
    if (!shadowContainer) return;

    const mobile = isMobile();
    const primaryColor = this.campaign?.styling.primaryColor || '#6366f1';

    // Create backdrop
    const backdrop = document.createElement('div');
    backdrop.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:2147483646;display:flex;align-items:center;justify-content:center;pointer-events:auto;';
    shadowContainer.appendChild(backdrop);

    const card = document.createElement('div');
    card.style.cssText = 'background:#1a1a2e;border-radius:20px;padding:32px 24px;max-width:340px;width:90%;text-align:center;';

    // Phone icon
    const icon = document.createElement('div');
    icon.style.cssText = `width:64px;height:64px;border-radius:50%;background:${primaryColor};display:flex;align-items:center;justify-content:center;margin:0 auto 16px;font-size:28px;`;
    icon.textContent = '📞';
    card.appendChild(icon);

    // Heading
    const heading = document.createElement('div');
    heading.style.cssText = 'color:#fff;font-size:20px;font-weight:700;margin-bottom:8px;';
    heading.textContent = (node.callConfig?.headingText) || node.label || 'Give Us a Call';
    card.appendChild(heading);

    // Sub text
    const sub = document.createElement('div');
    sub.style.cssText = 'color:rgba(255,255,255,0.6);font-size:14px;margin-bottom:20px;line-height:1.4;';
    sub.textContent = (node.callConfig?.subText) || 'Tap the button below to call us directly.';
    card.appendChild(sub);

    // Phone number display
    const numberDisplay = document.createElement('div');
    numberDisplay.style.cssText = 'color:#fff;font-size:24px;font-weight:600;letter-spacing:1px;margin-bottom:20px;';
    numberDisplay.textContent = phoneNumber;
    card.appendChild(numberDisplay);

    // Call button (tel: link on mobile, copy on desktop)
    const callBtn = document.createElement('a');
    callBtn.href = `tel:${phoneNumber.replace(/[^+\d]/g, '')}`;
    callBtn.style.cssText = `display:block;background:${primaryColor};color:#fff;padding:14px 24px;border-radius:12px;font-size:16px;font-weight:600;text-decoration:none;transition:opacity 0.2s;`;
    callBtn.textContent = (node.callConfig?.buttonText) || (mobile ? 'Call Now' : 'Call Us');
    callBtn.addEventListener('click', () => {
      this.trackEvent('cta_click', { nodeId: node.id, action: 'call', phoneNumber });
      this.emit('cta:click', { action: 'call', phoneNumber });
    });
    card.appendChild(callBtn);

    // Copy number button (desktop fallback)
    if (!mobile) {
      const copyBtn = document.createElement('button');
      copyBtn.style.cssText = 'background:none;border:1px solid rgba(255,255,255,0.2);color:rgba(255,255,255,0.7);padding:10px 16px;border-radius:8px;font-size:13px;cursor:pointer;margin-top:10px;width:100%;';
      copyBtn.textContent = 'Copy Number';
      copyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        navigator.clipboard.writeText(phoneNumber).then(() => {
          copyBtn.textContent = 'Copied!';
          setTimeout(() => { copyBtn.textContent = 'Copy Number'; }, 2000);
        });
      });
      card.appendChild(copyBtn);
    }

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.style.cssText = 'background:none;border:none;color:rgba(255,255,255,0.4);font-size:13px;cursor:pointer;margin-top:16px;padding:8px;';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => {
      backdrop.remove();
      this.show();
    });
    card.appendChild(closeBtn);

    backdrop.appendChild(card);

    // Track event
    this.trackEvent('overlay_shown', { nodeId: node.id, type: 'call', phoneNumber });
  }

  private async handleChoiceSelected(edgeId: string, choiceLabel: string): Promise<void> {
    if (!this.config || !this.campaign || !this.currentNodeId) return;

    // Track event
    this.trackEvent('choice_selected', {
      nodeId: this.currentNodeId,
      choiceEdgeId: edgeId,
      choiceLabel,
    });
    this.emit('overlay:choice', { edgeId, choiceLabel });

    // Call POST /v1/interaction to record choice and get next node
    const apiUrl = this.config.apiUrl || DEFAULT_API_URL;
    try {
      const response = await fetch(`${apiUrl}/v1/interaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteKey: this.config.siteKey,
          sessionId: this.sessionId,
          campaignId: this.campaign.id,
          nodeId: this.currentNodeId,
          choiceEdgeId: edgeId,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.nextNodeId) {
          this.navigateToNode(data.nextNodeId);
        } else {
          // No next node — close and show bubble
          this.closeVideo();
          this.show();
        }
      } else {
        logger.error('Failed to record interaction:', response.status);
      }
    } catch (err) {
      logger.error('Error calling /v1/interaction:', err);
      // Fallback: find the edge locally and navigate
      const edge = this.flowEdges.find((e) => e.id === edgeId);
      if (edge?.targetNodeId) {
        this.navigateToNode(edge.targetNodeId);
      }
    }
  }

  private checkOverlayTrigger(percent: number): void {
    if (this.overlayTriggered || !this.overlayChoices) return;

    if (percent >= this.overlayChoices.triggerPercent) {
      this.overlayTriggered = true;
      this.overlayChoices.show();
      // Track overlay_shown only when actually visible
      this.trackEvent('overlay_shown', { nodeId: this.currentNodeId, triggerPercent: this.overlayChoices.triggerPercent });
      this.emit('overlay:show', { nodeId: this.currentNodeId });
      logger.debug('Overlay choices shown at', percent + '%');
    }
  }

  private setupExitIntent(): void {
    if (!this.config?.exitIntent || !this.bubble) return;
    let lastCheck = 0;
    // Sprint 003 (2026-04-29): store handler reference so destroy() can clean up.
    const handler = (e: MouseEvent) => {
      // Throttle: skip if less than 100ms since last check
      const now = Date.now();
      if (now - lastCheck < 100) return;
      lastCheck = now;
      if (e.clientY < 20) {
        // Only trigger once per session
        try {
          if (sessionStorage.getItem('eb_exit_triggered')) return;
          sessionStorage.setItem('eb_exit_triggered', '1');
        } catch {
          // sessionStorage may not be available
        }
        document.removeEventListener('mousemove', handler);
        this.exitIntentHandler = null;
        // Only trigger if bubble is visible and video hasn't been opened
        if (!this.videoOpened) {
          this.bubble?.pulse();
          setTimeout(() => this.openVideo(), 600);
        } else {
          this.bubble?.pulse();
        }
      }
    };
    this.exitIntentHandler = handler;
    document.addEventListener('mousemove', handler);
  }

  private setupScrollTrigger(): void {
    const threshold = this.config?.scrollAutoOpen ?? this.campaign?.trigger.scrollOpenAt;
    if (threshold == null || threshold <= 0) return;
    // Sprint 003 (2026-04-29): store handler reference so destroy() can clean up.
    const handler = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      const scrollPercent = (scrollY / (docHeight - winHeight)) * 100;
      if (scrollPercent >= threshold) {
        window.removeEventListener('scroll', handler);
        this.scrollHandler = null;
        if (!this.videoOpened && !this.isDismissed()) {
          this.openVideo();
        }
      }
    };
    this.scrollHandler = handler;
    window.addEventListener('scroll', handler, { passive: true });
  }

  // ===========================================================================
  // Private Methods - Event Tracking
  // ===========================================================================

  private getStoryContext(): Record<string, unknown> {
    if ((this.campaign?.videos?.length ?? 0) <= 1) return {};
    return {
      storyMode: true,
      storyIndex: this.currentVideoIndex,
      storyTotal: this.campaign!.videos.length,
    };
  }

  // ===========================================================================
  // Attribution Cookie (_cr_engaged) for video-to-purchase tracking
  // ===========================================================================

  private updateAttributionCookie(eventType: string, data: Record<string, unknown>): void {
    try {
      if (eventType === 'video_play') {
        // Write cookie on first video play (don't overwrite if already exists with higher progress)
        const existing = this.readAttributionCookie();
        if (!existing) {
          this.writeAttributionCookie({
            sessionId: this.sessionId || '',
            campaignId: this.campaign?.id || '',
            campaignName: this.campaign?.name || '',
            videoId: (data.videoId as string) || '',
            maxProgress: 0,
            engagedAt: Date.now(),
            dismissed: false,
          });
        }
      } else if (eventType === 'video_progress') {
        const quartile = data.quartile as number;
        if (quartile > 0) {
          const existing = this.readAttributionCookie();
          if (existing && quartile > existing.maxProgress) {
            existing.maxProgress = quartile;
            this.writeAttributionCookie(existing);
          }
        }
      } else if (eventType === 'dismiss') {
        const existing = this.readAttributionCookie();
        if (existing) {
          existing.dismissed = true;
          this.writeAttributionCookie(existing);
        }
      }
    } catch {
      // Cookie operations should never break event tracking
    }
  }

  private readAttributionCookie(): {
    sessionId: string;
    campaignId: string;
    campaignName: string;
    videoId: string;
    maxProgress: number;
    engagedAt: number;
    dismissed: boolean;
  } | null {
    try {
      const match = document.cookie.split('; ').find((c) => c.startsWith('_cr_engaged='));
      if (!match) return null;
      return JSON.parse(decodeURIComponent(match.split('=')[1]));
    } catch {
      return null;
    }
  }

  private writeAttributionCookie(data: {
    sessionId: string;
    campaignId: string;
    campaignName: string;
    videoId: string;
    maxProgress: number;
    engagedAt: number;
    dismissed: boolean;
  }): void {
    const value = encodeURIComponent(JSON.stringify(data));
    document.cookie = `_cr_engaged=${value}; Max-Age=2592000; Path=/; SameSite=Lax`;

    // Sync to Shopify cart attributes for checkout attribution
    // (Customer Events pixels can't read cookies but can read cart customAttributes)
    this.syncToShopifyCart(data);
  }

  private shopifyCartSynced = false;

  private syncToShopifyCart(data: {
    sessionId: string;
    campaignId: string;
    campaignName: string;
    videoId: string;
    maxProgress: number;
    engagedAt: number;
    dismissed: boolean;
  }): void {
    // Only attempt on Shopify sites
    if (!(window as unknown as Record<string, unknown>).Shopify) return;

    // Debounce: don't hammer /cart/update.js on every quartile
    // Sync on first engagement (video_play) and on dismiss, skip intermediate progress
    if (this.shopifyCartSynced && data.maxProgress > 0 && data.maxProgress < 100 && !data.dismissed) {
      return;
    }

    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            _cr_session_id: data.sessionId,
            _cr_campaign_id: data.campaignId,
            _cr_campaign_name: data.campaignName,
            _cr_video_id: data.videoId,
            _cr_max_progress: String(data.maxProgress),
            _cr_engaged_at: String(data.engagedAt),
            _cr_dismissed: String(data.dismissed),
          },
        }),
        keepalive: true,
      }).catch(() => {
        // Silent fail — cart sync is best-effort
      });
      this.shopifyCartSynced = true;
      logger.debug('Synced attribution to Shopify cart attributes');
    } catch {
      // Silent fail
    }
  }

  private trackEvent(eventType: string, data: Record<string, unknown>): void {
    if (!this.config) return;

    // Separate videoId (top-level SDK field) from other data (goes into metadata)
    const { videoId, ...metadata } = data;

    // Auto-inject story context into all events when in story mode
    const enrichedMetadata = { ...metadata, ...this.getStoryContext() };

    const event: Record<string, unknown> = {
      type: eventType,
      siteKey: this.config.siteKey,
      sessionId: this.sessionId,
      campaignId: this.campaign?.id,
      timestamp: Date.now(),
      url: window.location.href,
      videoId,
      metadata: Object.keys(enrichedMetadata).length > 0 ? enrichedMetadata : undefined,
    };

    logger.debug('Tracking event:', event);

    // Fire to host page GA4/Meta Pixel for conversion path attribution
    fireAnalyticsBridgeEvent(eventType, data);

    // Update attribution cookie for video-to-purchase tracking
    this.updateAttributionCookie(eventType, data);

    // Buffer events and send to ingestion API
    this.sendEventToAPI(event);
  }

  private eventBuffer: Array<Record<string, unknown>> = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  private sendEventToAPI(event: Record<string, unknown>): void {
    this.eventBuffer.push(event);

    // Debounce: flush after 500ms of no new events, or immediately if buffer is large
    if (this.eventBuffer.length >= 5) {
      this.flushEvents();
    } else {
      if (this.flushTimer) clearTimeout(this.flushTimer);
      this.flushTimer = setTimeout(() => this.flushEvents(), 500);
    }
  }

  private flushEvents(): void {
    if (this.eventBuffer.length === 0 || !this.config) return;

    const events = [...this.eventBuffer];
    this.eventBuffer = [];
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    const apiUrl = this.config.apiUrl || DEFAULT_API_URL;
    logger.debug(`Flushing ${events.length} events to ${apiUrl}/v1/events`);

    // Strip internal _retries property before sending
    const cleanEvents = events.map(({ _retries, ...rest }) => rest);

    fetch(`${apiUrl}/v1/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: cleanEvents }),
      keepalive: true,
    }).catch((error) => {
      logger.error('Failed to send events, re-queuing:', error);
      // Re-queue failed events with retry tracking (max 3 retries, cap buffer at 50)
      const requeued = events
        .filter((e) => ((e._retries as number) ?? 0) < 3)
        .map((e) => ({ ...e, _retries: ((e._retries as number) ?? 0) + 1 }));
      if (this.eventBuffer.length + requeued.length <= 50) {
        this.eventBuffer.push(...requeued);
        // Sprint 003 (2026-04-29): schedule the next flush proactively with
        // exponential backoff instead of piggybacking on the next organic
        // sendEventToAPI call. Without this, a permanent network outage could
        // either (a) pin the buffer forever if no new events arrive, or (b)
        // cause 3 rapid-fire retries within 500ms during high event traffic.
        // First retry +1s, second +2s, third +4s.
        if (requeued.length > 0) {
          const minRetryCount = Math.min(...requeued.map((e) => (e._retries as number) ?? 1));
          const delayMs = 1000 * Math.pow(2, Math.max(0, minRetryCount - 1));
          if (this.flushTimer) clearTimeout(this.flushTimer);
          this.flushTimer = setTimeout(() => this.flushEvents(), delayMs);
        }
      }
    });
  }

  // ===========================================================================
  // Private Methods - Utilities
  // ===========================================================================

  private emit(event: ClickReelEvent, data?: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          logger.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  private extractYouTubeId(url: string): string | undefined {
    if (!url) return undefined;
    // Match youtube.com/watch?v=ID, youtu.be/ID, youtube.com/shorts/ID, youtube.com/embed/ID
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return undefined;
  }

  private extractTikTokId(url: string): string | undefined {
    if (!url) return undefined;
    // Match tiktok.com/@user/video/{id}
    const videoMatch = url.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/);
    if (videoMatch) return videoMatch[1];
    // Match tiktok.com/t/{shortcode}
    const shortMatch = url.match(/tiktok\.com\/t\/([a-zA-Z0-9]+)/);
    if (shortMatch) return shortMatch[1];
    // Match vm.tiktok.com/{shortcode}
    const vmMatch = url.match(/vm\.tiktok\.com\/([a-zA-Z0-9]+)/);
    if (vmMatch) return vmMatch[1];
    return undefined;
  }

  private isTikTokUrl(url: string): boolean {
    return /tiktok\.com|vm\.tiktok\.com/i.test(url);
  }

  private isInstagramUrl(url: string): boolean {
    return /instagram\.com\/(?:[^/]+\/)?(?:reels?|p)\//i.test(url);
  }

  private getYouTubeThumbnail(url: string): string {
    const id = this.extractYouTubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg` : '';
  }

  /**
   * Fetch TikTok video thumbnail via oEmbed API.
   * Uses the ingest proxy to avoid CORS issues on customer sites.
   * Falls back to direct oEmbed call if proxy unavailable.
   */
  private async fetchTikTokThumbnail(videoUrl: string): Promise<string> {
    const apiUrl = this.config?.apiUrl || '';

    // Strategy 1: Try via ingest proxy (returns thumbnailUrl alongside videoId)
    if (apiUrl) {
      try {
        const resp = await fetch(
          `${apiUrl}/v1/tiktok/resolve?url=${encodeURIComponent(videoUrl)}`,
          { signal: AbortSignal.timeout(5000) },
        );
        if (resp.ok) {
          const data = await resp.json();
          if (data.thumbnailUrl) return data.thumbnailUrl;
        }
      } catch {
        // Proxy failed, try direct oEmbed
      }
    }

    // Strategy 2: Try TikTok oEmbed directly (works when CORS is allowed)
    try {
      const resp = await fetch(
        `https://www.tiktok.com/oembed?url=${encodeURIComponent(videoUrl)}`,
        { signal: AbortSignal.timeout(5000) },
      );
      if (resp.ok) {
        const data = await resp.json();
        if (data.thumbnail_url) return data.thumbnail_url;
      }
    } catch {
      // oEmbed not available
    }

    return '';
  }

  private waitForDOM(): Promise<void> {
    return new Promise((resolve) => {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => resolve(), { once: true });
      } else {
        resolve();
      }
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private isDismissed(): boolean {
    return this.dismissedUntil > Date.now();
  }

  private loadDismissedState(): void {
    try {
      const stored = localStorage.getItem('clickreel_dismissed_until');
      if (stored) {
        this.dismissedUntil = parseInt(stored, 10);
      }
    } catch {
      // localStorage may not be available
    }
  }

  private saveDismissedState(): void {
    try {
      localStorage.setItem('clickreel_dismissed_until', String(this.dismissedUntil));
    } catch {
      // localStorage may not be available
    }
  }
}

// =============================================================================
// Create and Export Singleton Instance
// =============================================================================

const clickReel = new ClickReelSDK();

// Export for ES modules
export default clickReel;
export { clickReel as ClickReel };
export type { ClickReelSDK };

// =============================================================================
// Expose Global Namespace for IIFE/CDN Usage
// =============================================================================

if (typeof window !== 'undefined') {
  (window as Window & { ClickReel?: ClickReelSDK }).ClickReel = clickReel;
}

// Support for AMD/RequireJS (UMD compatibility)
declare const define: ((name: string, deps: string[], factory: () => unknown) => void) & { amd?: boolean };

if (typeof define === 'function' && define.amd) {
  define('ClickReel', [], () => clickReel);
}
